import React, { useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Moon, Factory, Sun, Zap, LucideIcon, ChevronDown,
  ClipboardList, ArrowLeftRight, FileText,
} from 'lucide-react'
import { DropdownMenu } from 'radix-ui'
import { useAuth } from '@nyos/shared'
import { useTheme, Theme } from '../contexts/ThemeContext'

/**
 * TopNav — primary navigation as a top row beneath GlobalHeader.
 *
 * Left side: route groups rendered as dropdown menus (route-aware active
 * state on the group label when any child route is active). `soon` items
 * route to the shared ComingSoonPage stub — roadmap affordance only.
 * Right side: quick-action drawer triggers (mid-shift tools) + theme
 * switcher. The old 'Inventory' button was removed — it was a mislabeled
 * track-inspector shortcut hardcoded to 'track-3'; the inspector already
 * opens by clicking a track on the yard board.
 *
 * Module map + role intent: docs/post-demo-backlog.md — Verification
 * Result Framework section.
 */

interface TopNavProps {
  onOpenTurnover: () => void
  onOpenSwitchJobs: () => void
  onOpenTurnins: () => void
}

interface NavItem {
  to: string
  label: string
  /** Exact path match for active state (root routes). */
  end?: boolean
  /** Routes to the shared ComingSoonPage stub. */
  soon?: boolean
  superAdminOnly?: boolean
  /** Visible to ADMIN (yard IT) and SUPERADMIN (vendor). */
  adminOnly?: boolean
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Operations',
    items: [
      { to: '/', label: 'Yard Board — Live', end: true },
      { to: '/execution/switch-jobs', label: 'Switch List Builder' },
      { to: '/operations/sweeps', label: 'Sweeps', soon: true },
      { to: '/operations/inventory', label: 'Inventory', soon: true },
    ],
  },
  {
    label: 'Interchange',
    items: [
      { to: '/dashboard/inbound-interchange', label: 'Inbound Interchange' },
      { to: '/interchange/outbound', label: 'Outbound — Class I Handoff', soon: true },
    ],
  },
  {
    label: 'Safety',
    items: [
      { to: '/safety/blue-flag', label: 'Blue Flag Protection', soon: true },
      { to: '/safety/exception-ledger', label: 'Exception Ledger', soon: true },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/insights', label: 'Insights', end: true },
      { to: '/insights/yard-dashboard', label: 'Yard Dashboard', soon: true },
      { to: '/insights/carbon-esg', label: 'Carbon & ESG', soon: true },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { to: '/revenue/demurrage', label: 'Demurrage & Dwell', soon: true },
      { to: '/revenue/billing', label: 'Billing & Invoices', soon: true },
    ],
  },
  {
    label: 'Customers',
    items: [
      { to: '/portal/shipper', label: 'Self-Service', soon: true },
      { to: '/portal/carbon', label: 'Carbon Corner', soon: true },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/admin/spatial-integrity', label: 'Spatial Integrity', adminOnly: true },
      // Role-gated: the item is a discovery affordance only — the route
      // guard and server DTO split are the real enforcement (Plan 26 1b).
      { to: '/admin/switch-trace', label: 'Switch Trace', superAdminOnly: true },
    ],
  },
]

const THEMES: { id: Theme; title: string; Icon: LucideIcon }[] = [
  { id: 'midnight', title: 'Midnight', Icon: Moon },
  { id: 'foundry', title: 'Foundry', Icon: Factory },
  { id: 'daylight', title: 'Daylight', Icon: Sun },
  { id: 'cyber', title: 'Cyber', Icon: Zap },
]

const TopNav: React.FC<TopNavProps> = ({
  onOpenTurnover,
  onOpenSwitchJobs,
  onOpenTurnins,
}) => {
  const { theme, setTheme } = useTheme()
  const { user } = useAuth()
  const { pathname } = useLocation()
  const isSuperAdmin = user?.role === 'ROLE_SUPERADMIN'
  const isAdmin = user?.role === 'ROLE_ADMIN'

  const utilityBtn =
    'px-2.5 py-1 rounded-md text-sm font-medium text-text-muted hover:text-text-primary hover:bg-hover transition-colors inline-flex items-center gap-1.5 pointer-coarse:min-h-11'

  const groupClass = (active: boolean) =>
    `px-3 h-10 flex items-center gap-1 text-sm font-medium border-b-2 transition-colors outline-none ${
      active
        ? 'border-accent-blue text-text-primary'
        : 'border-transparent text-text-muted hover:text-text-primary data-[state=open]:text-text-primary'
    }`

  const itemClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center justify-between gap-3 px-3 py-1.5 text-sm outline-none cursor-pointer rounded-sm data-[highlighted]:bg-hover data-[highlighted]:text-text-primary ${
      isActive ? 'text-text-primary font-medium' : 'text-text-muted'
    }`

  const moreItemClass =
    'flex items-center gap-2 px-3 py-1.5 text-sm outline-none cursor-pointer rounded-sm text-text-muted data-[highlighted]:bg-hover data-[highlighted]:text-text-primary pointer-coarse:min-h-11'

  const ActiveThemeIcon = THEMES.find((t) => t.id === theme)?.Icon ?? Moon

  const isItemActive = (item: NavItem) =>
    item.end ? pathname === item.to : pathname.startsWith(item.to)

  const themeIcons = useMemo(
    () =>
      THEMES.map(({ id, title, Icon }) => (
        <button
          key={id}
          onClick={() => setTheme(id)}
          title={`${title} theme`}
          className={`p-1 rounded-md transition-colors pointer-coarse:min-h-11 pointer-coarse:min-w-11 inline-flex items-center justify-center ${
            theme === id
              ? 'text-accent-blue bg-hover'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          <Icon size={13} />
        </button>
      )),
    [theme, setTheme]
  )

  return (
    <nav className="h-10 bg-bg-header border-b border-border flex items-center px-4 gap-1">
      {NAV_GROUPS.map((group) => {
        const items = group.items.filter(
          (i) => (!i.superAdminOnly || isSuperAdmin) && (!i.adminOnly || isSuperAdmin || isAdmin)
        )
        if (items.length === 0) return null
        const groupActive = items.some(isItemActive)
        return (
          <DropdownMenu.Root key={group.label}>
            <DropdownMenu.Trigger className={groupClass(groupActive)}>
              {group.label}
              <ChevronDown size={12} className="opacity-60" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="start"
                sideOffset={4}
                className="min-w-56 bg-bg-drawer border border-border rounded-md shadow-lg p-1 z-50"
              >
                {items.map((item) => (
                  <DropdownMenu.Item key={item.to} asChild>
                    <NavLink to={item.to} end={item.end} className={itemClass}>
                      {item.label}
                      {item.soon && (
                        <span className="ml-2 shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-blue/40 bg-accent-blue/15 text-accent-blue">
                          Soon
                        </span>
                      )}
                    </NavLink>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )
      })}
      <div className="flex-1" />
      {/* TODO(tablet-refactor): utility cluster + divider could become a
          `UtilityCluster` component with per-APP_TARGET visibility/density
          config (via useDevice) when a tablet target needs fewer or larger
          controls — keep border-l separator inside it. */}
      <div className="ml-2 pl-3 border-l border-border flex items-center gap-1">
        <button className={utilityBtn} onClick={onOpenSwitchJobs}>
          <ClipboardList size={14} />
          Job Queue
        </button>
        {/* T3 (plan-30): below 1100px Turnover + Turn-ins collapse into the
            More overflow menu — overflow, not hide; the demo path uses
            both drawers. Width media query only, never useDevice(). */}
        <button className={`${utilityBtn} hidden min-[1100px]:inline-flex`} onClick={onOpenTurnover}>
          <ArrowLeftRight size={14} />
          Shift Turnover
        </button>
        <button className={`${utilityBtn} hidden min-[1100px]:inline-flex`} onClick={onOpenTurnins}>
          <FileText size={14} />
          Turn-ins
        </button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            className={`${utilityBtn} min-[1100px]:hidden pointer-coarse:min-h-11 pointer-coarse:min-w-11`}
          >
            <ChevronDown size={14} />
            More
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={4}
              className="min-w-48 bg-bg-drawer border border-border rounded-md shadow-lg p-1 z-50"
            >
              <DropdownMenu.Item className={moreItemClass} onSelect={onOpenTurnover}>
                <ArrowLeftRight size={14} />
                Shift Turnover
              </DropdownMenu.Item>
              <DropdownMenu.Item className={moreItemClass} onSelect={onOpenTurnins}>
                <FileText size={14} />
                Turn-ins
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      <div className="ml-3 pl-3 border-l border-border flex items-center gap-1.5">
        <span className="hidden min-[1100px]:contents">{themeIcons}</span>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            title="Theme"
            className="min-[1100px]:hidden p-1 rounded-md text-text-muted hover:text-text-primary inline-flex items-center justify-center pointer-coarse:min-h-11 pointer-coarse:min-w-11"
          >
            <ActiveThemeIcon size={14} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={4}
              className="min-w-40 bg-bg-drawer border border-border rounded-md shadow-lg p-1 z-50"
            >
              {THEMES.map(({ id, title, Icon }) => (
                <DropdownMenu.Item
                  key={id}
                  className={`${moreItemClass} ${theme === id ? 'text-text-primary font-medium' : ''}`}
                  onSelect={() => setTheme(id)}
                >
                  <Icon size={14} />
                  {title}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </nav>
  )
}

export default React.memo(TopNav)
